/**
 * Foreva Jewels event system - shared behaviour for the event page and popup.
 *
 * Responsibilities:
 *   - countdown ticking (page hero + popup)
 *   - client-side state re-derive, because Shopify full-page-caches and Liquid's
 *     `'now'` is render time, so the upcoming -> live flip can lag
 *   - .ics generation (CRLF, escaped, 75-octet folded)
 *   - popup show / dismiss with a TTL, Esc, focus trap, reduced motion
 *
 * Every date it reads is ISO-8601 with an offset, so Date parsing is unambiguous.
 */
(function () {
  'use strict';

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  function pad(n) { return String(n).padStart(2, '0'); }

  // Scroll lock is refcounted: on /pages/jck the popup and the lightbox can both be
  // open, and a naive remove() on either close would unlock the page under the other.
  var lockCount = 0;
  function lockScroll() { if (++lockCount === 1) document.body.classList.add('fje-popup-open'); }
  function unlockScroll() { if (--lockCount <= 0) { lockCount = 0; document.body.classList.remove('fje-popup-open'); } }

  // Top-layer stack so Escape only reaches the topmost overlay.
  var layers = [];
  function pushLayer(n) { layers.push(n); }
  function popLayer(n) { var i = layers.lastIndexOf(n); if (i > -1) layers.splice(i, 1); }
  function isTop(n) { return layers[layers.length - 1] === n; }

  function focusablesIn(el) {
    return Array.prototype.filter.call(
      el.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null; }
    );
  }

  function trapTab(el, e) {
    var f = focusablesIn(el);
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function parse(v) {
    var t = Date.parse(v);
    return isNaN(t) ? null : t;
  }

  /** Derive upcoming | live | ended from the same data the server used. */
  function deriveState(startTs, endTs) {
    var now = Date.now();
    if (now >= endTs) return 'ended';
    if (now >= startTs) return 'live';
    return 'upcoming';
  }

  // storage, safe in private mode where setItem throws
  var memStore = {};
  function storeGet(key) {
    try { var v = localStorage.getItem(key); if (v !== null) return v; } catch (e) {}
    try { var v2 = sessionStorage.getItem(key); if (v2 !== null) return v2; } catch (e) {}
    return Object.prototype.hasOwnProperty.call(memStore, key) ? memStore[key] : null;
  }
  function storeSet(key, val) {
    try { localStorage.setItem(key, val); return; } catch (e) {}
    try { sessionStorage.setItem(key, val); return; } catch (e) {}
    memStore[key] = val;
  }

  // countdown
  function startCountdown(el, targetTs, onReached) {
    var d = el.querySelector('[data-d]');
    var h = el.querySelector('[data-h]');
    var m = el.querySelector('[data-m]');
    var s = el.querySelector('[data-s]');
    if (!d || !h || !m || !s) return null;

    var id = null;
    function tick() {
      var diff = targetTs - Date.now();
      if (diff <= 0) { if (id) clearInterval(id); if (onReached) onReached(); return; }
      var secs = Math.floor(diff / 1000);
      var mins = Math.floor(secs / 60); secs %= 60;
      var hrs = Math.floor(mins / 60); mins %= 60;
      var days = Math.floor(hrs / 24); hrs %= 24;
      d.textContent = days;
      h.textContent = pad(hrs);
      m.textContent = pad(mins);
      s.textContent = pad(secs);
    }
    tick();
    id = setInterval(tick, 1000);
    return id;
  }

  // .ics
  function icsEscape(v) {
    return String(v)
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');
  }

  /** RFC 5545: fold at 75 octets, continuation lines start with a space. */
  function fold(line) {
    var out = [];
    var buf = line;
    while (buf.length > 75) {
      out.push(buf.slice(0, 75));
      buf = ' ' + buf.slice(75);
    }
    out.push(buf);
    return out.join('\r\n');
  }

  function buildICS(cfg) {
    var stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Foreva Jewels//Events//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:' + cfg.uid,
      'DTSTAMP:' + stamp,
      'DTSTART;VALUE=DATE:' + cfg.start,
      'DTEND;VALUE=DATE:' + cfg.end,
      'SUMMARY:' + icsEscape(cfg.title),
      'LOCATION:' + icsEscape(cfg.location),
      'DESCRIPTION:' + icsEscape(cfg.description || cfg.title),
      'URL:' + cfg.url,
      'END:VEVENT',
      'END:VCALENDAR'
    ];
    return lines.map(fold).join('\r\n') + '\r\n';
  }

  function downloadICS(cfg) {
    var blob = new Blob([buildICS(cfg)], { type: 'text/calendar;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = cfg.filename || 'event.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 0);
  }

  // event landing page
  function initPage() {
    var root = document.querySelector('[data-fj-event]');
    if (!root) return;

    var startTs = parse(root.getAttribute('data-start'));
    var endTs = parse(root.getAttribute('data-end'));
    var forced = root.getAttribute('data-force-state');

    var cd = root.querySelector('[data-fj-countdown]');
    if (cd && startTs) {
      startCountdown(cd, startTs, function () {
        if (forced === 'auto') location.reload();
      });
    }

    // Cached HTML may claim "upcoming" when the show has already started.
    if (forced === 'auto' && startTs && endTs) {
      var actual = deriveState(startTs, endTs);
      if (actual !== root.getAttribute('data-state')) location.reload();
    }

    var icsBtn = root.querySelector('[data-fj-ics]');
    if (icsBtn) {
      icsBtn.addEventListener('click', function () {
        downloadICS({
          uid: root.getAttribute('data-uid'),
          start: root.getAttribute('data-ics-start'),
          end: root.getAttribute('data-ics-end'),
          title: root.getAttribute('data-title'),
          location: root.getAttribute('data-location'),
          description: root.getAttribute('data-title') + ' - ' + root.getAttribute('data-url'),
          url: root.getAttribute('data-url'),
          filename: root.getAttribute('data-filename')
        });
      });
    }
  }

  // floating button: shown when no popup is pending (skipped, dismissed, or not on this page)
  var popupPending = false;
  var fabListeners = [];
  function popupDone() { popupPending = false; fabListeners.splice(0).forEach(function (fn) { fn(); }); }

  function initFab() {
    var fab = document.querySelector('[data-fj-fab]');
    if (!fab || fab.getAttribute('data-fj-ready') === '1') return;
    fab.setAttribute('data-fj-ready', '1');
    if (fab.parentElement !== document.body) document.body.appendChild(fab);
    function show() { fab.hidden = false; }
    if (popupPending) fabListeners.push(show);
    else setTimeout(show, 600);
  }

  // popup
  function initPopup() {
    var overlay = document.querySelector('[data-fj-popup]');
    if (!overlay) return;
    if (overlay.getAttribute('data-fj-ready') === '1') return;
    overlay.setAttribute('data-fj-ready', '1');
    popupPending = true;

    var designMode = !!(window.Shopify && window.Shopify.designMode);
    var key = overlay.getAttribute('data-storage-key') || 'fj_event_dismissed';
    var ttlHours = parseFloat(overlay.getAttribute('data-ttl-hours')) || 72;
    var delay = parseInt(overlay.getAttribute('data-delay'), 10);
    if (isNaN(delay)) delay = 800;

    if (!designMode) {
      var stored = storeGet(key);
      if (stored && (Date.now() - Number(stored)) < ttlHours * 3600000) { popupDone(); return; }
    }

    var startTs = parse(overlay.getAttribute('data-start'));
    var endTs = parse(overlay.getAttribute('data-end'));
    var forced = overlay.getAttribute('data-force-state');

    // Cached HTML safety net: never show a popup for a finished show.
    if (forced === 'auto' && endTs && Date.now() >= endTs) { popupDone(); return; }

    var dialog = overlay.querySelector('[data-fj-popup-dialog]');
    if (!dialog) { popupDone(); return; }
    var lastFocused = null;

    // position:fixed breaks inside a transformed ancestor - reparent to be safe.
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

    var cd = overlay.querySelector('[data-fj-countdown]');
    if (cd && startTs) {
      startCountdown(cd, startTs, function () { if (forced === 'auto') location.reload(); });
    }

    function onKeydown(e) {
      // only the topmost overlay reacts to Escape
      if (e.key === 'Escape') { if (!isTop('popup')) return; e.preventDefault(); dismiss(); return; }
      if (e.key === 'Tab') trapTab(dialog, e);
    }

    function open() {
      lastFocused = document.activeElement;
      overlay.hidden = false;
      overlay.removeAttribute('aria-hidden');
      lockScroll();
      pushLayer('popup');
      document.addEventListener('keydown', onKeydown);
      var closeBtn = overlay.querySelector('[data-fj-popup-close]');
      if (closeBtn) closeBtn.focus(); else dialog.focus();
    }

    function dismiss() {
      if (!designMode) storeSet(key, String(Date.now()));
      document.removeEventListener('keydown', onKeydown);
      popLayer('popup');
      unlockScroll();
      if (reduceMotion) {
        overlay.hidden = true;
      } else {
        overlay.style.opacity = '0';
        overlay.style.transition = 'opacity .2s ease';
        setTimeout(function () {
          overlay.hidden = true;
          overlay.style.opacity = '';
          overlay.style.transition = '';
        }, 200);
      }
      if (lastFocused && lastFocused.focus) lastFocused.focus();
      popupDone();
    }

    var closers = overlay.querySelectorAll('[data-fj-popup-close], [data-fj-popup-dismiss]');
    Array.prototype.forEach.call(closers, function (b) { b.addEventListener('click', dismiss); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) dismiss(); });

    if (designMode || reduceMotion) open();
    else setTimeout(open, delay);
  }


  // gallery lightbox
  function initGallery() {
    var overlay = document.querySelector('[data-fj-lightbox]');
    var strip = document.querySelector('[data-fj-gallery]');
    if (!overlay || !strip) return;

    // boot() re-runs on shopify:section:load, which re-renders the section while the
    // previously body-appended overlay is still attached. Drop the orphan first, or we
    // end up with a duplicate id and a dead listener set.
    var stale = document.getElementById(overlay.id);
    if (stale && stale !== overlay) stale.remove();
    if (overlay.getAttribute('data-fj-ready') === '1') return;
    overlay.setAttribute('data-fj-ready', '1');

    var triggers = Array.prototype.slice.call(strip.querySelectorAll('[data-fj-gal-open]'));
    if (!triggers.length) return;

    // position:fixed breaks inside a transformed ancestor - reparent to be safe.
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

    var dialog = overlay.querySelector('[data-fj-lb-dialog]');
    var imgEl = overlay.querySelector('[data-fj-lb-img]');
    var fig = overlay.querySelector('[data-fj-lb-fig]');
    var iEl = overlay.querySelector('[data-fj-lb-i]');
    var closeBtn = overlay.querySelector('[data-fj-lb-close]');
    if (!dialog || !imgEl) return;

    var items = triggers.map(function (t) {
      var thumb = t.querySelector('img');
      return { full: t.getAttribute('data-full'), alt: thumb ? thumb.getAttribute('alt') : '', trigger: t };
    });
    var idx = 0;
    var lastFocused = null;

    function preload(n) {
      var it = items[n];
      if (it && it.full) { var p = new Image(); p.src = it.full; }
    }

    function show(n) {
      idx = (n + items.length) % items.length;   // wrap: 7 -> 1, 1 -> 7
      var it = items[idx];
      imgEl.src = it.full;
      imgEl.alt = it.alt || '';
      if (iEl) iEl.textContent = String(idx + 1);
      preload(idx + 1);
      preload(idx - 1);
    }

    function onKeydown(e) {
      if (!isTop('lightbox')) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(idx + 1); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(idx - 1); return; }
      if (e.key === 'Home') { e.preventDefault(); show(0); return; }
      if (e.key === 'End') { e.preventDefault(); show(items.length - 1); return; }
      if (e.key === 'Tab') trapTab(dialog, e);
    }

    function open(n) {
      lastFocused = document.activeElement;
      show(n);
      overlay.hidden = false;
      overlay.removeAttribute('aria-hidden');
      lockScroll();
      pushLayer('lightbox');
      document.addEventListener('keydown', onKeydown);
      (closeBtn || dialog).focus();
    }

    function close() {
      document.removeEventListener('keydown', onKeydown);
      popLayer('lightbox');
      unlockScroll();
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      // return focus to the photo actually being viewed, not the one first clicked,
      // so arrow-nav then Escape leaves the user in the right place
      var back = (items[idx] && items[idx].trigger) || lastFocused;
      if (back && back.focus) {
        back.focus();
        var slide = back.closest ? back.closest('slideshow-slide') : null;
        if (slide && slide.scrollIntoView) {
          slide.scrollIntoView({ inline: 'start', block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    }

    triggers.forEach(function (t, n) {
      t.addEventListener('click', function (e) { e.preventDefault(); open(n); });
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    var prev = overlay.querySelector('[data-fj-lb-prev]');
    var next = overlay.querySelector('[data-fj-lb-next]');
    if (prev) prev.addEventListener('click', function () { show(idx - 1); });
    if (next) next.addEventListener('click', function () { show(idx + 1); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });

    // swipe: Pointer Events cover touch and mouse-drag in one path
    if (fig) {
      var sx = 0, sy = 0, st = 0, down = false;
      fig.addEventListener('pointerdown', function (e) {
        down = true; sx = e.clientX; sy = e.clientY; st = Date.now();
        if (fig.setPointerCapture) { try { fig.setPointerCapture(e.pointerId); } catch (err) {} }
      });
      fig.addEventListener('pointerup', function (e) {
        if (!down) return;
        down = false;
        var dx = e.clientX - sx, dy = e.clientY - sy, dt = Date.now() - st;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 600) {
          show(dx < 0 ? idx + 1 : idx - 1);
        }
      });
      fig.addEventListener('pointercancel', function () { down = false; });
    }

    // slideshow.js marks off-screen slides aria-hidden; mirror that onto the trigger's
    // tabindex so focusable buttons never sit inside an aria-hidden subtree.
    var scroller = strip.querySelector('slideshow-slides');
    if (scroller && window.MutationObserver) {
      var sync = function () {
        triggers.forEach(function (t) {
          var slide = t.closest ? t.closest('slideshow-slide') : null;
          if (!slide) return;
          if (slide.getAttribute('aria-hidden') === 'true') t.setAttribute('tabindex', '-1');
          else t.removeAttribute('tabindex');
        });
      };
      new MutationObserver(sync).observe(scroller, {
        subtree: true, attributes: true, attributeFilter: ['aria-hidden']
      });
      sync();
    }
  }

  function boot() { initPage(); initPopup(); initFab(); initGallery(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Re-init when the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', boot);
})();
